import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Paper,
  CircularProgress,
  Typography,
  Menu,
  MenuItem,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Translate as TranslateIcon, History as HistoryIcon } from '@mui/icons-material';
import apiService from '../../services/apiService';

import { useTranslation } from 'react-i18next';

interface TermsPrivacyModalsProps {
  termsOpen: boolean;
  privacyOpen: boolean;
  onTermsClose: () => void;
  onPrivacyClose: () => void;
}

const TermsPrivacyModals: React.FC<TermsPrivacyModalsProps> = ({
  termsOpen,
  privacyOpen,
  onTermsClose,
  onPrivacyClose,
}) => {
  const { t, i18n } = useTranslation();
  const [termsContent, setTermsContent] = useState('');
  const [termsContentFr, setTermsContentFr] = useState('');
  const [termsTitle, setTermsTitle] = useState('Terms of Service');
  const [termsTitleFr, setTermsTitleFr] = useState('Conditions d\'utilisation');
  const [privacyContent, setPrivacyContent] = useState('');
  const [privacyContentFr, setPrivacyContentFr] = useState('');
  const [privacyTitle, setPrivacyTitle] = useState('Privacy Policy');
  const [privacyTitleFr, setPrivacyTitleFr] = useState('Politique de confidentialité');
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [loadingPrivacy, setLoadingPrivacy] = useState(false);

  const [isTranslatingTerms, setIsTranslatingTerms] = useState(false);
  const [showTranslatedTerms, setShowTranslatedTerms] = useState(false);
  const [translatedTermsContent, setTranslatedTermsContent] = useState('');
  const [translatedTermsTitle, setTranslatedTermsTitle] = useState('');

  const [isTranslatingPrivacy, setIsTranslatingPrivacy] = useState(false);
  const [showTranslatedPrivacy, setShowTranslatedPrivacy] = useState(false);
  const [translatedPrivacyContent, setTranslatedPrivacyContent] = useState('');
  const [translatedPrivacyTitle, setTranslatedPrivacyTitle] = useState('');

  const [targetLang, setTargetLang] = useState({ code: 'en', name: 'English', flag: '🇺🇸' });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isRtl = ['ar', 'fa', 'ur', 'dari', 'he'].includes(targetLang.code);

  const ALL_LANGUAGES = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'fa', name: 'Farsi', flag: '/assets/images/iran_lion_sun.png', isImage: true },
    { code: 'ur', name: 'Urdu', flag: '🇵🇰' },
    { code: 'dari', name: 'Dari', flag: '🇦🇫' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
    { code: 'zh-CN', name: 'Simplified Chinese', flag: '🇨🇳' },
    { code: 'zh-TW', name: 'Traditional Chinese', flag: '🇹🇼' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'he', name: 'Hebrew', flag: '🇮🇱' },
    { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
    { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
    { code: 'pl', name: 'Polish', flag: '🇵🇱' },
    { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
    { code: 'el', name: 'Greek', flag: '🇬🇷' },
  ];

  // 0 for English, 1 for French
  const isFrench = i18n.language.startsWith('fr');
  const langTab = isFrench ? 1 : 0;

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  const fetchTermsContent = async () => {
    if (termsContent) return; // Already loaded
    setLoadingTerms(true);
    try {
      const response = await axios.get(`${apiUrl}/content/terms`);
      if (response.data.success && response.data.data) {
        setTermsTitle(response.data.data.title || 'Terms of Service');
        setTermsTitleFr(response.data.data.titleFr || 'Conditions d\'utilisation');
        setTermsContent(response.data.data.content || '');
        setTermsContentFr(response.data.data.contentFr || '');
      }
    } catch (err) {
      console.error('Error fetching terms:', err);
    } finally {
      setLoadingTerms(false);
    }
  };

  const fetchPrivacyContent = async () => {
    if (privacyContent) return; // Already loaded
    setLoadingPrivacy(true);
    try {
      const response = await axios.get(`${apiUrl}/content/privacy`);
      if (response.data.success && response.data.data) {
        setPrivacyTitle(response.data.data.title || 'Privacy Policy');
        setPrivacyTitleFr(response.data.data.titleFr || 'Politique de confidentialité');
        setPrivacyContent(response.data.data.content || '');
        setPrivacyContentFr(response.data.data.contentFr || '');
      }
    } catch (err) {
      console.error('Error fetching privacy:', err);
    } finally {
      setLoadingPrivacy(false);
    }
  };

  useEffect(() => {
    if (termsOpen) {
      fetchTermsContent();
    }
  }, [termsOpen]);

  useEffect(() => {
    if (privacyOpen) {
      fetchPrivacyContent();
    }
  }, [privacyOpen]);

  const handleTranslateTerms = async () => {
    if (showTranslatedTerms) {
      setShowTranslatedTerms(false);
      return;
    }
    if (translatedTermsContent) {
      setShowTranslatedTerms(true);
      return;
    }
    try {
      setIsTranslatingTerms(true);
      const currentContent = isFrench ? termsContentFr : termsContent;
      const currentTitle = isFrench ? termsTitleFr : termsTitle;
      const [contentRes, titleRes] = await Promise.all([
        apiService.translateText(currentContent, targetLang.code),
        apiService.translateText(currentTitle, targetLang.code)
      ]);
      if (contentRes.success && contentRes.data) setTranslatedTermsContent(contentRes.data.translatedText);
      if (titleRes.success && titleRes.data) setTranslatedTermsTitle(titleRes.data.translatedText);
      setShowTranslatedTerms(true);
    } catch (err) {
      console.error('Terms translation failed:', err);
    } finally {
      setIsTranslatingTerms(false);
    }
  };

  const handleTranslatePrivacy = async () => {
    if (showTranslatedPrivacy) {
      setShowTranslatedPrivacy(false);
      return;
    }
    if (translatedPrivacyContent) {
      setShowTranslatedPrivacy(true);
      return;
    }
    try {
      setIsTranslatingPrivacy(true);
      const currentContent = isFrench ? privacyContentFr : privacyContent;
      const currentTitle = isFrench ? privacyTitleFr : privacyTitle;
      const [contentRes, titleRes] = await Promise.all([
        apiService.translateText(currentContent, targetLang.code),
        apiService.translateText(currentTitle, targetLang.code)
      ]);
      if (contentRes.success && contentRes.data) setTranslatedPrivacyContent(contentRes.data.translatedText);
      if (titleRes.success && titleRes.data) setTranslatedPrivacyTitle(titleRes.data.translatedText);
      setShowTranslatedPrivacy(true);
    } catch (err) {
      console.error('Privacy translation failed:', err);
    } finally {
      setIsTranslatingPrivacy(false);
    }
  };

  const handleLanguageSelect = (lang: any) => {
    setTargetLang(lang);
    setAnchorEl(null);
    // Clear old translations
    setTranslatedTermsContent('');
    setTranslatedTermsTitle('');
    setShowTranslatedTerms(false);
    setTranslatedPrivacyContent('');
    setTranslatedPrivacyTitle('');
    setShowTranslatedPrivacy(false);
  };

  const formatContent = (text: string) => {
    if (!text) return null;
    const hasHTML = /<[a-z][\s\S]*>/i.test(text);
    const contentStyles = {
      direction: isRtl && (showTranslatedTerms || showTranslatedPrivacy) ? 'rtl' : 'ltr',
      textAlign: isRtl && (showTranslatedTerms || showTranslatedPrivacy) ? 'right' : 'left',
      '& *': { fontFamily: 'inherit !important' },
      '& h1, & h2, & h3, & h4, & h5, & h6': {
        fontWeight: '600 !important',
        mt: 4,
        mb: 2,
        color: '#0f172a !important',
        lineHeight: 1.3,
      },
      '& h1': { fontSize: '4rem !important' },
      '& h2': { fontSize: '3.5rem !important' },
      '& h3': { fontSize: '3rem !important' },
      '& h4': { fontSize: '2.5rem !important' },
      '& h5': { fontSize: '2.25rem !important' },
      '& h6': { fontSize: '2rem !important' },
      '& p': {
        fontSize: '2.25rem !important',
        lineHeight: 1.8,
        color: '#334155 !important',
        mb: 2,
        marginTop: 0,
      },
      '& ul, & ol': { pl: 3, mb: 2, marginTop: 0 },
      '& li': {
        fontSize: '2.25rem !important',
        lineHeight: 1.8,
        color: '#334155 !important',
        mb: 1.5,
      },
      '& strong, & b': {
        fontWeight: '600 !important',
        color: '#0f172a !important',
      },
      '& em, & i': { fontStyle: 'italic !important' },
      '& code': {
        backgroundColor: '#f1f5f9 !important',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '1.8em !important',
        fontFamily: 'monospace !important',
        color: '#334155 !important',
      },
      '& pre': {
        backgroundColor: '#f1f5f9 !important',
        padding: '16px',
        borderRadius: '8px',
        overflow: 'auto',
        mb: 2,
        '& code': { backgroundColor: 'transparent !important', padding: 0 },
      },
      '& blockquote': {
        borderLeft: '4px solid #3b82f6 !important',
        pl: 2,
        ml: 0,
        fontStyle: 'italic',
        color: '#64748b !important',
        mb: 2,
      },
      '& a': {
        color: '#3b82f6 !important',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
      },
    };
    if (hasHTML) {
      return (
        <Box
          className="content-wrapper"
          sx={contentStyles}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      );
    }
    return (
      <Box className="content-wrapper" sx={contentStyles}>
        <ReactMarkdown>{text}</ReactMarkdown>
      </Box>
    );
  };

  return (
    <>
      {/* Terms of Service Modal */}
      <Dialog
        open={termsOpen}
        onClose={onTermsClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e5e7eb',
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, flexShrink: 0 }}>
              {showTranslatedTerms ? translatedTermsTitle : (isFrench ? termsTitleFr : termsTitle)}
            </Typography>
            <Button
              variant="outlined"
              size="large"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: '900', fontSize: '1.5rem', py: 1, px: 2, display: 'inline-flex', alignItems: 'center' }}
            >
              {'isImage' in targetLang && targetLang.isImage ? (
                <Box component="img" src={targetLang.flag} sx={{ width: 24, height: 18, borderRadius: '2px', objectFit: 'cover', mr: 1.5 }} />
              ) : (
                <span style={{ marginRight: targetLang.flag ? 8 : 0 }}>{targetLang.flag}</span>
              )}
              {targetLang.name}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleTranslateTerms}
              disabled={isTranslatingTerms}
              startIcon={showTranslatedTerms ? <HistoryIcon sx={{ fontSize: '1.8rem !important' }} /> : <TranslateIcon sx={{ fontSize: '1.8rem !important' }} />}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: '900',
                fontSize: '1.6rem',
                py: 1,
                px: 3,
                animation: !showTranslatedTerms ? 'pulse-glow 2s infinite' : 'none',
                '@keyframes pulse-glow': {
                  '0%': { transform: 'scale(1)', boxShadow: '0 4px 8px rgba(16, 185, 129, 0.2)' },
                  '50%': { transform: 'scale(1.05)', boxShadow: '0 8px 16px rgba(16, 185, 129, 0.4)' },
                  '100%': { transform: 'scale(1)', boxShadow: '0 4px 8px rgba(16, 185, 129, 0.2)' }
                }
              }}
            >
              {isTranslatingTerms ? t('common.translating') : (showTranslatedTerms ? t('common.showOriginal') : t('common.translateTo', { language: targetLang.name }))}
            </Button>
          </Box>
          <IconButton
            onClick={onTermsClose}
            sx={{ color: '#6b7280' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: '16px', mb: 3, fontSize: '1.8rem', fontWeight: '800', border: '2px solid #10b981', color: '#065f46', textAlign: 'center' }}>
            {t('common.legalDisclaimer')}
          </Box>
          {loadingTerms ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: '8px',
                backgroundColor: '#ffffff',
              }}
            >
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                {showTranslatedTerms ? (
                  formatContent(translatedTermsContent)
                ) : (
                  !isFrench ? (
                    termsContent ? formatContent(termsContent) : <Typography>Content not available</Typography>
                  ) : (
                    termsContentFr ? formatContent(termsContentFr) : <Typography>Contenu non disponible en français</Typography>
                  )
                )}
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
          <Button
            onClick={onTermsClose}
            variant="contained"
            sx={{
              bgcolor: '#10b981',
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {isFrench ? 'Fermer' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Privacy Policy Modal */}
      <Dialog
        open={privacyOpen}
        onClose={onPrivacyClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e5e7eb',
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, flexShrink: 0 }}>
              {showTranslatedPrivacy ? translatedPrivacyTitle : (isFrench ? privacyTitleFr : privacyTitle)}
            </Typography>
            <Button
              variant="outlined"
              size="large"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: '900', fontSize: '1.5rem', py: 1, px: 2, display: 'inline-flex', alignItems: 'center' }}
            >
              {'isImage' in targetLang && targetLang.isImage ? (
                <Box component="img" src={targetLang.flag} sx={{ width: 24, height: 18, borderRadius: '2px', objectFit: 'cover', mr: 1.5 }} />
              ) : (
                <span style={{ marginRight: targetLang.flag ? 8 : 0 }}>{targetLang.flag}</span>
              )}
              {targetLang.name}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleTranslatePrivacy}
              disabled={isTranslatingPrivacy}
              startIcon={showTranslatedPrivacy ? <HistoryIcon sx={{ fontSize: '1.8rem !important' }} /> : <TranslateIcon sx={{ fontSize: '1.8rem !important' }} />}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: '900',
                fontSize: '1.6rem',
                py: 1,
                px: 3,
                animation: !showTranslatedPrivacy ? 'pulse-glow 2s infinite' : 'none',
                '@keyframes pulse-glow': {
                  '0%': { transform: 'scale(1)', boxShadow: '0 4px 8px rgba(16, 185, 129, 0.2)' },
                  '50%': { transform: 'scale(1.05)', boxShadow: '0 8px 16px rgba(16, 185, 129, 0.4)' },
                  '100%': { transform: 'scale(1)', boxShadow: '0 4px 8px rgba(16, 185, 129, 0.2)' }
                }
              }}
            >
              {isTranslatingPrivacy ? t('common.translating') : (showTranslatedPrivacy ? t('common.showOriginal') : t('common.translateTo', { language: targetLang.name }))}
            </Button>
          </Box>
          <IconButton
            onClick={onPrivacyClose}
            sx={{ color: '#6b7280' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: '16px', mb: 3, fontSize: '1.8rem', fontWeight: '800', border: '2px solid #10b981', color: '#065f46', textAlign: 'center' }}>
            {t('common.legalDisclaimer')}
          </Box>
          {loadingPrivacy ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: '8px',
                backgroundColor: '#ffffff',
              }}
            >
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                {showTranslatedPrivacy ? (
                  formatContent(translatedPrivacyContent)
                ) : (
                  !isFrench ? (
                    privacyContent ? formatContent(privacyContent) : <Typography>Content not available</Typography>
                  ) : (
                    privacyContentFr ? formatContent(privacyContentFr) : <Typography>Contenu non disponible en français</Typography>
                  )
                )}
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
          <Button
            onClick={onPrivacyClose}
            variant="contained"
            sx={{
              bgcolor: '#10b981',
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {isFrench ? 'Fermer' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mt: 1,
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
            maxHeight: '400px'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {ALL_LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageSelect(lang)}
            selected={targetLang.code === lang.code}
            sx={{
              py: 2,
              px: 3,
              gap: 2,
              fontWeight: targetLang.code === lang.code ? 900 : 700,
              '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.05)' }
            }}
          >
            {'isImage' in lang && lang.isImage ? (
              <Box component="img" src={lang.flag} sx={{ width: 32, height: 24, borderRadius: '4px', objectFit: 'cover' }} />
            ) : (
              <Box sx={{ fontSize: '2.5rem' }}>{lang.flag}</Box>
            )}
            <Typography variant="h5" sx={{ fontWeight: 'inherit' }}>{lang.name}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default TermsPrivacyModals;

